import { Snackbar } from "@dropbox/dig-components/snackbar";
import { UIIcon } from "@dropbox/dig-icons";
import { CheckmarkCircleLine, FailLine } from "@dropbox/dig-icons/assets";
import type { ClickableEmptyChatInstructionsOption } from "@mirage/mosaics/Chat/components/chat/instructions/EmptyChatInstructions";
import { useComposeVoicesContext } from "@mirage/mosaics/ComposeAssistant/data/ComposeVoicesContext";
import type { UserMessageOptions } from "@mirage/service-conversation/types";
import type { ComposeVoice } from "@mirage/shared/compose/compose-voice";
import { SnackbarView } from "@mirage/shared/snackbar/SnackbarView";
import i18n from "@mirage/translations";
import React, { useCallback, useState } from "react";

interface UseAIToolLibraryVoicesProps {
  onVoiceChange: (voiceId: string, voiceName: string) => void;
  setUserMessageOptions: (options: UserMessageOptions) => void;
  setEmptyChatOption: (option: ClickableEmptyChatInstructionsOption) => void;
  onCloseLibrary?: () => void;
  currentVoiceID?: string | null;
  onPostMessageWithVoice: (voiceId: string) => void;
  markdownDraft: string;
}

interface UseAIToolLibraryVoicesReturn {
  voices: ComposeVoice[];
  selectedVoiceId: string | null;
  handleVoiceChange: (voiceId: string, voiceName: string) => void;
  handleUseVoice: (voiceID: string, voiceName: string) => void;
  saveVoice: (voice: ComposeVoice) => Promise<void | string>;
  deleteVoice: (voiceId: string) => Promise<void>;
  setVoiceSnackbarMsg: (
    msg: { message: string; type: "success" | "error" } | undefined,
  ) => void;
  voiceSettingsSnackbar: React.ReactNode;
  handleSetCreatedVoiceSnackbarMsg: (msg: string | undefined) => void;
}

export const useAIToolLibraryVoices = ({
  onVoiceChange,
  setUserMessageOptions,
  setEmptyChatOption,
  onCloseLibrary,
  currentVoiceID,
  onPostMessageWithVoice,
  markdownDraft,
}: UseAIToolLibraryVoicesProps): UseAIToolLibraryVoicesReturn => {
  const [voiceSnackbarMsg, setVoiceSnackbarMsg] = useState<
    { message: string; type: "success" | "error" } | undefined
  >(undefined);

  const {
    voices = [],
    saveVoice: baseVoiceSave,
    deleteVoice: baseVoiceDelete,
  } = useComposeVoicesContext();

  const handleVoiceChange = useCallback(
    (voiceId: string, voiceName: string) => {
      onVoiceChange?.(voiceId, voiceName);
    },
    [onVoiceChange],
  );

  const handleUseVoice = useCallback(
    (voiceID: string, voiceName: string) => {
      if (markdownDraft?.length > 0) {
        onPostMessageWithVoice(voiceID);
      } else {
        setEmptyChatOption("write");
        setUserMessageOptions({ agentIds: ["write"] });
        handleVoiceChange(voiceID, voiceName);
      }
      onCloseLibrary?.();
    },
    [
      handleVoiceChange,
      onCloseLibrary,
      setEmptyChatOption,
      setUserMessageOptions,
      onPostMessageWithVoice,
      markdownDraft,
    ],
  );

  const saveVoice = useCallback(
    async (voice: ComposeVoice) => {
      try {
        await baseVoiceSave(voice);
        setVoiceSnackbarMsg({
          message: i18n.t(
            "compose_assistant_save_individual_snackbar_message_templates",
          ),
          type: "success",
        });
      } catch (error) {
        setVoiceSnackbarMsg({
          message: i18n.t("compose_generic_error"),
          type: "error",
        });
        throw error;
      }
    },
    [baseVoiceSave],
  );

  const deleteVoice = useCallback(
    async (voiceId: string) => {
      try {
        await baseVoiceDelete(voiceId);
        setVoiceSnackbarMsg({
          message: i18n.t(
            "compose_assistant_save_individual_snackbar_message_templates",
          ),
          type: "success",
        });
      } catch (error) {
        setVoiceSnackbarMsg({
          message: i18n.t("compose_generic_error"),
          type: "error",
        });
        throw error;
      }
    },
    [baseVoiceDelete],
  );

  const handleSetCreatedVoiceSnackbarMsg = useCallback(
    (msg: string | undefined) => {
      if (msg) {
        setVoiceSnackbarMsg({
          message: msg,
          type: "success",
        });
      }
    },
    [],
  );

  const voiceSettingsSnackbar = voiceSnackbarMsg && (
    <Snackbar.Position>
      <SnackbarView
        title={voiceSnackbarMsg.message}
        onClose={() => setVoiceSnackbarMsg(undefined)}
        accessory={
          <UIIcon
            src={
              voiceSnackbarMsg.type === "error" ? FailLine : CheckmarkCircleLine
            }
          />
        }
        timeoutMs={3000}
      />
    </Snackbar.Position>
  );

  return {
    voices,
    selectedVoiceId: currentVoiceID || null,
    handleVoiceChange,
    handleUseVoice,
    saveVoice,
    deleteVoice,
    setVoiceSnackbarMsg,
    voiceSettingsSnackbar,
    handleSetCreatedVoiceSnackbarMsg,
  };
};
